import { actionTypesApi } from '@/api'
import type { ActionType, UpdateActionTypeRequest } from '@/api/actionTypesApi'
import { getTenantForApiCall } from '@/api/axiosInstance'
import { useAuthStore } from '@/stores/authStore'
import { ORGANIZATION_ACTION_TYPES, isActionTypeActive } from '@/utils/actionTypeUtils'
import { type PageError, extractErrorMessage, toPageError } from '@/utils/errorHandler'
import { isOwnerOfOrganization } from '@/utils/permissions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

const EMPTY_ACTION_TYPES: ActionType[] = []
/** Types a user never triggers by hand, so no dropdown offers them. */
const EXCLUDED_ACTION_TYPES = new Set(['cancel_action', 'launch_shell'])
/** Action types change through the settings page, which invalidates; between edits the list is stable. */
const ACTION_TYPES_STALE_MS = 60_000

export interface ActionTypeBatchOutcome {
	done: string[]
	failed: Array<{ name: string; reason: string }>
}

async function settleBatch(names: string[], write: (name: string) => Promise<void>): Promise<ActionTypeBatchOutcome> {
	const results = await Promise.allSettled(names.map(write))
	const outcome: ActionTypeBatchOutcome = { done: [], failed: [] }
	results.forEach((result, index) => {
		const name = names[index]
		if (result.status === 'fulfilled') {
			outcome.done.push(name)
		} else {
			outcome.failed.push({ name, reason: extractErrorMessage(result.reason, 'something went wrong') })
		}
	})
	return outcome
}

export const actionTypeKeys = {
	all: ['actionTypes'] as const,
	list: (tenantId: string) => [...actionTypeKeys.all, 'list', tenantId] as const,
}

/** The org Operations page has no types of its own: its three intents stand in for them. */
export const ORG_ACTION_TYPES: ActionType[] = ORGANIZATION_ACTION_TYPES.map(type => ({
	type,
	payload_type: 'json',
	active: true,
}))

export interface UseActionTypesOptions {
	enabled?: boolean
	/** Pass 0 where the list must be re read on every open, as the role editor does. */
	staleTime?: number
}

export interface UseActionTypesResult {
	actionTypes: ActionType[]
	isLoading: boolean
	isFetching: boolean
	error: PageError | null
	refetch: () => Promise<unknown>
}

/** Every action type of the current project, `renew_cert` already stripped by the API layer. */
export function useActionTypes({ enabled = true, staleTime = ACTION_TYPES_STALE_MS }: UseActionTypesOptions = {}) {
	const tenantId = getTenantForApiCall()
	const { data, isPending, isFetching, error, refetch } = useQuery({
		queryKey: actionTypeKeys.list(tenantId),
		queryFn: () => actionTypesApi.getActionTypes(),
		enabled: enabled && Boolean(tenantId),
		staleTime,
	})
	return {
		actionTypes: data ?? EMPTY_ACTION_TYPES,
		isLoading: enabled && isPending,
		isFetching,
		error: error ? toPageError(error, 'Failed to load action types') : null,
		refetch,
	} satisfies UseActionTypesResult
}

/** Active types a user can pick to trigger, in the order the server returned them. */
export function useDropdownActionTypes(options?: UseActionTypesOptions): ActionType[] {
	const { actionTypes } = useActionTypes(options)
	return useMemo(
		() =>
			actionTypes.filter(actionType => isActionTypeActive(actionType) && !EXCLUDED_ACTION_TYPES.has(actionType.type)),
		[actionTypes],
	)
}

/** The org intents for an owner of `organizationId`, nothing for anyone else; no request involved. */
export function useOrgActionTypes(organizationId: string | undefined): {
	actionTypes: ActionType[]
	isLoading: boolean
} {
	const user = useAuthStore(state => state.user)
	const isOwner = organizationId ? isOwnerOfOrganization(user, organizationId) : false
	return { actionTypes: isOwner ? ORG_ACTION_TYPES : EMPTY_ACTION_TYPES, isLoading: false }
}

/** The four writes, each settling only after the list has been re read so callers see the change. */
export function useActionTypeMutations() {
	const queryClient = useQueryClient()
	const invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey: actionTypeKeys.all }), [queryClient])
	const create = useMutation({
		mutationFn: (actionType: ActionType) => actionTypesApi.createActionType(actionType),
		onSuccess: invalidate,
	})
	const update = useMutation({
		mutationFn: ({ name, changes }: { name: string; changes: UpdateActionTypeRequest }) =>
			actionTypesApi.updateActionType(name, changes),
		onSuccess: invalidate,
	})
	// A selection is one write per type but one re read for all of them, and every write is attempted:
	// the outcome names what went through and what did not, so the caller can say so.
	const deactivate = useMutation({
		mutationFn: (names: string[]) => settleBatch(names, name => actionTypesApi.deactivateActionType(name)),
		onSettled: invalidate,
	})
	const reactivate = useMutation({
		mutationFn: (names: string[]) => settleBatch(names, name => actionTypesApi.reactivateActionType(name)),
		onSettled: invalidate,
	})
	return {
		createActionType: create.mutateAsync,
		updateActionType: useCallback(
			(name: string, changes: UpdateActionTypeRequest) => update.mutateAsync({ name, changes }),
			[update.mutateAsync],
		),
		/** Attempts every named type and resolves with what went through and what failed. */
		deactivateActionTypes: deactivate.mutateAsync,
		reactivateActionTypes: reactivate.mutateAsync,
	}
}
